import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";

const getScoreColor = (score) => {
  if (score >= 800) return "#10B981";
  if (score >= 600) return "#3B82F6";
  if (score >= 400) return "#F59E0B";
  return "#EF4444";
};

const getScoreFill = (score) => {
  if (score >= 800) return "rgba(16,185,129,0.25)";
  if (score >= 600) return "rgba(59,130,246,0.25)";
  if (score >= 400) return "rgba(245,158,11,0.25)";
  return "rgba(239,68,68,0.25)";
};

export default function WorldMap({ projects = [] }) {
  const navigate = useNavigate();
  const mapRef = useRef(null);

  // Center of South Africa
  const center = [-27.0, 26.0];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="world-map">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <MapPin className="w-5 h-5 text-[#3B82F6]" /> Project Locations — South Africa
      </h3>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Map */}
        <div className="flex-1 h-[420px] rounded-xl overflow-hidden border border-slate-200" data-testid="leaflet-map">
          <MapContainer
            center={center}
            zoom={6}
            scrollWheelZoom={true}
            style={{ height: "100%", width: "100%" }}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {projects.map((project) => {
              const score = project.score || 0;
              const color = getScoreColor(score);
              const fill = getScoreFill(score);
              const radius = 30 + (score / 1000) * 25;

              return (
                <CircleMarker
                  key={project.id}
                  center={[project.coordinates.lat, project.coordinates.lng]}
                  radius={radius}
                  pathOptions={{
                    color: color,
                    fillColor: fill,
                    fillOpacity: 0.6,
                    weight: 3,
                    opacity: 0.9,
                  }}
                  eventHandlers={{
                    click: () => navigate(`/projects/${project.id}`),
                  }}
                  data-testid={`map-marker-${project.id}`}
                >
                  <Tooltip direction="top" offset={[0, -radius]} opacity={1} permanent={false}>
                    <div className="min-w-[180px]">
                      <p className="font-bold text-slate-900 text-sm">{project.name}</p>
                      <p className="text-xs text-slate-500 mb-2">{project.location}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div>
                          <span className="text-slate-500">Score:</span>{" "}
                          <span className="font-bold" style={{ color }}>{score}/1000</span>
                        </div>
                        <div>
                          <span className="text-slate-500">ROI:</span>{" "}
                          <span className="font-bold text-emerald-600">{project.roi_percentage}%</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Price:</span>{" "}
                          <span className="font-bold">${project.token_price}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Type:</span>{" "}
                          <span className="font-bold">{project.industry}</span>
                        </div>
                      </div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>

        {/* Legend */}
        <div className="lg:w-64 space-y-4">
          <h4 className="font-semibold text-slate-900">Projects Legend</h4>

          {/* Score Legend */}
          <div className="space-y-2">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Score Rating</p>
            {[
              { color: "bg-emerald-500", label: "800–1000 (Excellent)" },
              { color: "bg-blue-500", label: "600–799 (Good)" },
              { color: "bg-amber-500", label: "400–599 (Fair)" },
              { color: "bg-red-500", label: "0–399 (Needs Work)" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${item.color}`} />
                <span className="text-sm text-slate-600">{item.label}</span>
              </div>
            ))}
          </div>

          {/* Project List */}
          <div className="space-y-2 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-500 uppercase tracking-wide">All Projects</p>
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-slate-50 transition-all"
                data-testid={`legend-project-${project.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{project.name}</p>
                    <p className="text-xs text-slate-500">{project.industry}</p>
                  </div>
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: getScoreColor(project.score || 0) }}
                  >
                    {project.score || 0}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
